import { Box, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import clsx from 'clsx'
import { ChangeEventHandler, FC, SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { catchError, EMPTY, finalize, from, takeUntil } from 'rxjs'
import { AnalyticApi, AuthApi } from 'src/api'
import { Button, Input, RequiredAsterisk, useAnalytic } from 'src/components'
import { GroupRadio } from 'src/components/group-radio'
import { PRIVACY_POLICY_URL, TERMS_AND_SERVICE_URL } from 'src/constants'
import { ETrackingEvent } from 'src/enums'
import { useDebounceAnalyticInput, useUnsubscribe, useValidation } from 'src/hooks'
import { Logo } from 'src/images'
import { browserHistory, ERoutes, generate } from 'src/router'
import { SnackbarService } from 'src/services'
import { EventUtils, getApiErrorMessage } from 'src/utils'
import { workEmailSchema } from 'src/validation'
import { object, string } from 'yup'
import Style from './style.module.scss'

const schema = object().shape({
  email: workEmailSchema.fields.email,
  code: string().required('This field is required!')
})

interface IProps {
  inviteToken?: string
  onSignIn?: () => void
}

export const RegisterEmailForm: FC<IProps> = ({ inviteToken, onSignIn }) => {
  const { analytic, eventHandler } = useAnalytic('signup0')
  const { analyticInput } = useDebounceAnalyticInput('signup0')
  const unsubscribe$ = useUnsubscribe()
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [isSent, setIsSent] = useState(false)
  const [formData, setFormData] = useState({
    isRecruiter: false,
    email: '',
    code: ''
  })
  const { errors, validate } = useValidation({
    data: formData,
    schema
  })

  const [view, setView] = useState<'selectUserType' | 'registerEmail'>('selectUserType')

  const userTypeOptions = [
    { label: 'Hiring Manager', value: false },
    { label: 'Talent Agent (3rd-Party Recruiter)', value: true }
  ]

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    e.persist()
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (name === 'email') {
      setIsSent(false)
      setCountdown(0)
      analyticInput(ETrackingEvent.INPUT_WORK_EMAIL, value)
    }
  }, [analyticInput])

  const onGetCode = useCallback(async (e: Event | SyntheticEvent) => {
    EventUtils.preventDefault(e)
    const { isValid, value } = await validate({
      data: formData,
      schema: workEmailSchema
    })
    if (!isValid || !value) {
      return
    }

    const promise = AuthApi.signUpRequest({
      email: formData.email,
      inviteToken,
      sessionId: AnalyticApi._session
    }, { params: { type: 'code' } })

    setLoading(true)
    from(promise)
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error: AxiosError) => {
          errors.setError('email', getApiErrorMessage(error))
          SnackbarService.error(getApiErrorMessage(error))
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(() => {
        setIsSent(true)
        setCountdown(60)
      })
  }, [errors, formData, inviteToken, unsubscribe$, validate])

  const onVerifyCode = useCallback(async (e: Event | SyntheticEvent) => {
    EventUtils.preventDefault(e)
    analytic(ETrackingEvent.BTN_CREATE_ACCOUNT)

    const { isValid } = await validate()
    if (!isValid) {
      return
    }

    setLoading(true)
    from(AuthApi.signUpVerifyCode(formData))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error: AxiosError) => {
          SnackbarService.error(getApiErrorMessage(error))
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(({ data }) => {
        if (data.token) {
          browserHistory.push(generate(ERoutes.SIGN_UP, {
            inviteToken,
            token: data.token,
            isRecruiter: formData.isRecruiter || undefined,
            redirectTo: window.location.pathname + window.location.search
          }), formData.email)
        }
      })
  }, [analytic, formData, inviteToken, unsubscribe$, validate])

  const goToLogin = useCallback(() => {
    if (onSignIn) {
      return onSignIn()
    }
    browserHistory.push(generate(ERoutes.SIGN_IN))
  }, [onSignIn])

  useEffect(() => {
    if (countdown > 0) {
      const timeout = setTimeout(() => {
        setCountdown((prev) => Math.max(prev - 1, 0))
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [countdown])

  /**
   * Temporary bypass for the user type selection
   * Always sign up as a hiring manager
   */
  useEffect(() => {
    setView('registerEmail')
  }, [eventHandler])

  return (
    <form
      className={Style.signUpForm}
      onSubmit={!isSent ? eventHandler(ETrackingEvent.SIGN_UP_BTN_GET_CODE, onGetCode) : onVerifyCode}
    >
      <div className="mb-4">
        <img src={Logo} alt="logo"/>
      </div>

      <div className={Style.LoginHeader}>
        <Typography className={Style.Title}>Sign Up to KNOWME<span>TM</span></Typography>
        <Typography align="center" className={Style.Description}>
          Hiring
        </Typography>
      </div>

      <div className="fx-column gap-6">
        <div className={clsx('fx-column gap-8', view === 'registerEmail' && 'd-none')}>
          <div className="semibold-16 txt-black-01">Are you a Hiring Manager or Talent Agent?</div>
          <GroupRadio
            className="fx-column gap-6"
            defaultValue={formData.isRecruiter}
            name="isRecruiter"
            options={userTypeOptions}
            onChange={(isRecruiter) => {
              analyticInput(ETrackingEvent.SIGN_UP_RADIO_SELECT_USER_TYPE, isRecruiter)
              return setFormData((prev) => ({
                ...prev,
                isRecruiter: isRecruiter as boolean
              }))
            }}
          />

          <Button
            className="round-16"
            disabled={loading}
            onClick={eventHandler(ETrackingEvent.SIGN_UP_BTN_NEXT, () => setView('registerEmail'))}
          >
            Next
          </Button>
        </div>

        <div className={clsx('fx-column gap-6', view === 'selectUserType' && 'd-none')}>
          {!isSent
            ? (
              <Input
                autoFocus
                label={<>Work email<RequiredAsterisk/></>}
                id="email"
                type="email"
                name="email"
                placeholder="name@company.com"
                readOnly={isSent}
                disabled={loading}
                value={formData.email || ''}
                onChange={onChange}
                error={errors.getError('email') === 'Looks like you already have an account. Try signing in.' ? true : errors.getError('email')}
              />
            )
            : (
              <div className="heading-14">
                A verification code has been sent to <strong>{formData.email}</strong>. Please check your inbox.
              </div>
            )}

          {errors.getError('email') === 'Looks like you already have an account. Try signing in.' && (
            <div className="fx gap-1" style={{ marginTop: '-20px' }}>
              <span className="fx gap-1 txt-negative-500">
                {/* <IconWarning size={16}/> */}
                Email already registered, sign in through
              </span>
              <b
                className="pointer txt-color-blue-500"
                onClick={goToLogin}
              >
                Hiring Manager Sign In
              </b>
            </div>
          )}

          {isSent && (
            <div className="fx-column gap-1">
              <Input
                autoFocus
                label={<>Verification code<RequiredAsterisk/></>}
                id="code"
                type="text"
                name="code"
                placeholder="Enter the code sent to your email"
                value={formData.code || ''}
                onChange={onChange}
                onClick={eventHandler(ETrackingEvent.SIGN_UP_INPUT_CODE)}
                error={errors.getError('code')}
                required
              />
              <div className="body-12 txt-black-02">
                Didn’t receive the code?
                &nbsp;
                <strong>
                  {countdown > 1
                    ? <span style={{ color: '#C0C0C0' }}>Resend ({countdown}s)</span>
                    : (
                      <span
                        className="pointer txt-color-blue-500"
                        onClick={eventHandler(ETrackingEvent.SIGN_UP_BTN_GET_CODE, onGetCode)}
                      >
                        Resend
                      </span>
                    )}
                </strong>
              </div>
            </div>
          )}

          {!isSent
            ? (
              <Button
                className="round-16"
                disabled={loading}
                onClick={eventHandler(ETrackingEvent.SIGN_UP_BTN_GET_CODE, onGetCode)}
              >
                Get Verification Code
              </Button>
            )
            : (
              <Button
                className="round-16"
                type="submit"
                disabled={loading}
                onClick={onVerifyCode}
              >
                Create Account
              </Button>
            )}
        </div>

        <div className="body-14 txt-color-neutral-theme-300">
          By signing up, you accept KnowMe’s
          <b
            className="pointer txt-color-blue-500"
            onClick={() => window.open(TERMS_AND_SERVICE_URL, '_blank')}
          >
            &nbsp;Terms of Use&nbsp;
          </b>
          and acknowledge our
          <b
            className="pointer txt-color-blue-500"
            onClick={() => window.open(PRIVACY_POLICY_URL, '_blank')}
          >
            &nbsp;Privacy Policy
          </b>
          .
        </div>

        <Box className={Style.Box}>
          <Typography
            variant="body2-medium"
            color="var(--color-neutral-theme-900)"
          >
            You already have an account?
          </Typography>
          <b
            className="pointer txt-color-blue-500"
            onClick={eventHandler(ETrackingEvent.BTN_SIGNIN, goToLogin)}
          >
            Sign in
          </b>
        </Box>
      </div>
    </form>
  )
}
